import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { AcupunctureNeedle, MoxaStick, CuppingGlass } from './TCMIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/oEypR6uu5QmdPH4wi3iPeo/MOCKUP_MTC.png',
  alt: 'Kit Medicina Tradicional China - Sistema clínico completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-121">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-celesteLight/20 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-122"></div>

      {/* Floating TCM decor */}
      <AcupunctureNeedle className="hidden md:block absolute top-32 left-[6%] w-5 h-24 opacity-30 animate-float-soft -z-10" color="#0F766E" style={{
      transform: 'rotate(20deg)'
    }} data-id="element-123" />
      <AcupunctureNeedle className="hidden md:block absolute top-48 right-[8%] w-5 h-24 opacity-30 animate-float-soft -z-10" color="#0EA5E9" style={{
      transform: 'rotate(-25deg)',
      animationDelay: '1.2s'
    }} data-id="element-124" />
      <CuppingGlass className="hidden md:block absolute bottom-40 left-[4%] w-16 h-16 opacity-40 animate-float-soft -z-10" style={{
      animationDelay: '2s'
    }} data-id="element-125" />
      <MoxaStick className="hidden md:block absolute bottom-32 right-[5%] w-7 h-28 opacity-50 animate-float-soft -z-10" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-126" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-127">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-128">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-green/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-129">
            <span className="text-sm" data-id="element-130">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-greenDark" data-id="element-131">
              LUMEN AKASHA™
            </span>
          </div>

          <h1 className="sm:text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-4 max-w-4xl mx-auto text-[22px] leading-tight text-brand-greenDark" data-id="element-132">
            Todo tu sistema clínico. Listo. Desde hoy.
          </h1>
          <p className="md:text-2xl text-brand-celesteDark mb-8 font-bold font-serif text-[13px]" data-id="element-133">
            Kit Medicina Tradicional China™ — Combo Completo
          </p>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[260px] sm:max-w-xs md:max-w-md lg:max-w-lg mx-auto mb-2 flex justify-center" data-id="element-134">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '1/1',
            maxHeight: '420px'
          }} data-id="element-135">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-136" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-137">
            <p className="md:text-xl font-bold text-brand-green font-serif text-[13px] leading-relaxed" data-id="element-138">
              El sistema N°1 para terapeutas en Medicina Tradicional China en
              Argentina — Todos los niveles y cuadros clínicos
            </p>
            <p className="md:text-base text-brand-text/80 font-sans text-[12px]" data-id="element-139">
              Protocolos + recursos clínicos + fichas de paciente + Hierbas
              recomendadas + agenda llena listos para usar.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-140">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-green/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-141">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-green shrink-0" data-id="element-142" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-green/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-143">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-green shrink-0" data-id="element-144" />
              Acceso en 5 min
            </div>

            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-green/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-145">
              <span className="mr-1 md:mr-1.5 text-brand-green shrink-0" data-id="element-146">
                🌿
              </span>
              Todos los niveles
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-147">
            <CTAButton variant="purple" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-148">
              <span className="relative z-10" data-id="element-149">
                🌿 → QUIERO MI KIT MTC AHORA
              </span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-150">
              <div className="flex items-center" data-id="element-151">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-green" data-id="element-152" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-153">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-154">
                🖨️ Listo para imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-155">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-green" data-id="element-156" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-157">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-green" data-id="element-158" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-green/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-159">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-160">
          <div className="flex items-center" data-id="element-161">
            <span className="font-bold text-brand-green mr-1.5" data-id="element-162">+200</span>
            terapeutas
          </div>
          <div className="flex items-center" data-id="element-163">
            <span className="font-bold text-brand-green mr-1.5" data-id="element-164">1</span>
            Tomo principal
          </div>
          <div className="flex items-center" data-id="element-165">
            <span className="font-bold text-brand-green mr-1.5" data-id="element-166">4</span> Bonus
          </div>
          <div className="flex items-center" data-id="element-167">
            <Star className="w-3.5 h-3.5 text-brand-celesteDark fill-brand-celesteDark mr-1" data-id="element-168" />
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-169">
          <div className="flex items-center" data-id="element-170">
            <span className="font-bold text-brand-green mr-2" data-id="element-171">+200</span>
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-celeste/50 shrink-0" data-id="element-172"></div>
          <div className="flex items-center" data-id="element-173">
            <span className="font-bold text-brand-green mr-2" data-id="element-174">1</span> Tomo
            principal
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-celeste/50 shrink-0" data-id="element-175"></div>
          <div className="flex items-center" data-id="element-176">
            <span className="font-bold text-brand-green mr-2" data-id="element-177">4</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-celeste/50 shrink-0" data-id="element-178"></div>
          <div className="flex items-center" data-id="element-179">
            <Star className="w-4 h-4 text-brand-celesteDark fill-brand-celesteDark mr-1" data-id="element-180" />
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};